import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram, Facebook, Mail, Phone, MapPin, Globe, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";
import NewsletterSignup from "./NewsletterSignup";

const QUICK_LINKS = [
  { label: "About Us", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Tools", href: "#tools" },
  { label: "Why Axomind", href: "#why" },
  { label: "Blogs", href: "#blogs" },
  { label: "FAQs", href: "#faqs" },
  { label: "Contact", href: "#contact" },
];

const SERVICES_LINKS = [
  "Mutual Funds", "SIP Planning", "Goal-Based Investing",
  "Retirement Planning", "Tax Saving (ELSS)", "HNI Portfolio Services",
];

const LEGAL = [
  { label: "Privacy Policy", to: "/legal/privacy-policy" },
  { label: "Terms of Use", to: "/legal/terms-of-use" },
  { label: "Risk Disclosure", to: "/legal/risk-disclosure" },
  { label: "Disclaimer", to: "/legal/disclaimer" },
];

const REGULATORY = [
  {
    label: "KIM / SID / SAI",
    href: "https://www.sebi.gov.in/filings/mutual-funds.html",
    desc: "SEBI mutual fund filings — scheme documents",
  },
  {
    label: "AMFI Code of Conduct",
    href: "https://www.amfiindia.com/uploads/Revised_Codeof_Conductfor_Mutual_Fund_Distributors_April2022_57d91fe1c4.pdf",
    desc: "Official code of conduct for MFDs (PDF)",
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative bg-[#030303] border-t border-white/10 pt-20 pb-8">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

      {/* Newsletter row */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="glass-strong rounded-3xl p-8 lg:p-10 grid md:grid-cols-2 gap-6 items-center mb-16 border-[#D4AF37]/20">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Stay informed</p>
            <h3 className="font-display font-bold text-2xl lg:text-3xl text-white mt-3">
              Weekly investing insights, straight to your inbox.
            </h3>
            <p className="text-sm text-gray-400 mt-2">
              Curated market commentary, tax updates and behavioural-finance reads. No spam, ever.
            </p>
          </div>
          <NewsletterSignup />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-4">
            <img
              src={SITE.logoUrl}
              alt="Axomind Asset Services LLP logo"
              className="w-44 lg:w-48 h-auto object-contain shrink-0"
            />
            <div data-testid="footer-brand" className="flex flex-col items-start min-w-0">
              <div className="brand-wordmark text-white text-2xl">AXOMIND</div>
              <div className="brand-rule w-full mt-1.5 mb-1.5" />
              <div className="brand-subtitle text-[#D4AF37] text-[0.7rem]">
                Asset&nbsp;Services&nbsp;LLP
              </div>
              <div className="text-[12px] leading-snug text-gray-400 mt-1.5" data-testid="footer-amfi-tagline">
                AMFI Registered Mutual Fund Distributor | ARN-365843
              </div>
            </div>
          </div>
          <p className="mt-5 text-sm text-gray-400 leading-relaxed max-w-sm">
            AMFI Registered Mutual Fund Distributor (ARN-365843). Helping individuals, families and businesses build
            long-term wealth through disciplined, research-driven investing.
          </p>

          <div className="mt-6 space-y-3 text-sm">
            <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-3 text-gray-300 hover:text-[#D4AF37] transition" data-testid="footer-phone">
              <Phone size={14} className="text-[#D4AF37] shrink-0" /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-gray-300 hover:text-[#D4AF37] transition break-all" data-testid="footer-email">
              <Mail size={14} className="text-[#D4AF37] shrink-0" /> {SITE.email}
            </a>
            <a href={SITE.websiteUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-gray-300 hover:text-[#D4AF37] transition" data-testid="footer-website">
              <Globe size={14} className="text-[#D4AF37] shrink-0" /> {SITE.website}
            </a>
            <div className="flex items-start gap-3 text-gray-300" data-testid="footer-address">
              <MapPin size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="leading-relaxed">{SITE.address}</span>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            {[
              { icon: Linkedin, href: SITE.socials.linkedin, label: "LinkedIn" },
              { icon: Twitter, href: SITE.socials.twitter, label: "Twitter" },
              { icon: Instagram, href: SITE.socials.instagram, label: "Instagram" },
              { icon: Facebook, href: SITE.socials.facebook, label: "Facebook" },
            ].map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition flex items-center justify-center"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Navigate</div>
          <ul className="space-y-2.5 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <a href={l.href} className="text-gray-400 hover:text-white transition">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Services</div>
          <ul className="space-y-2.5 text-sm">
            {SERVICES_LINKS.map((s) => (
              <li key={s}><a href="#services" className="text-gray-400 hover:text-white transition">{s}</a></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Legal &amp; Regulatory</div>
          <ul className="space-y-2.5 text-sm">
            {LEGAL.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-gray-400 hover:text-white transition"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-6 text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Investor Resources</div>
          <ul className="space-y-3 text-sm">
            {REGULATORY.map((r) => (
              <li key={r.label}>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-reg-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group inline-flex items-start gap-2 text-gray-300 hover:text-[#D4AF37] transition"
                >
                  <ExternalLink size={14} className="mt-0.5 text-[#D4AF37] shrink-0" />
                  <span>
                    <span className="font-medium">{r.label}</span>
                    <span className="block text-[11px] text-gray-500 group-hover:text-gray-400">{r.desc}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14">
        <div
          data-testid="footer-arn-registration"
          className="mb-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-6 py-4"
        >
          <span className="text-sm text-gray-300">
            <span className="text-[#D4AF37] font-semibold">Registration Number:</span>{" "}
            <span className="font-semibold text-white">ARN-365843</span>
          </span>
          <span className="hidden sm:inline text-[#D4AF37]/50">|</span>
          <span className="text-sm text-gray-300">
            <span className="text-[#D4AF37] font-semibold">ARN Validity:</span>{" "}
            <span className="font-semibold text-white">21-Jul-2026 to 20-Jul-2029</span>
          </span>
        </div>

        <div className="glass rounded-2xl p-6 text-xs text-gray-500 leading-relaxed">
          <span className="text-[#D4AF37] font-semibold">DISCLOSURE:</span> Axomind Asset Services LLP is an AMFI
          Registered Mutual Fund Distributor (ARN-365843). We are NOT a SEBI-registered Investment Adviser and do not
          provide investment advisory or financial planning services. We distribute Regular Plans of mutual fund
          schemes and earn trail commissions from the respective Asset Management Companies (AMCs); scheme-wise
          commission structures are available on request. Mutual fund investments are subject to market risks. Read
          all scheme related documents carefully before investing. Past performance is not indicative of future
          results. The information on this website is for investor education purposes only and does not constitute
          investment suggestions, an offer or solicitation to buy or sell any security. Investors should consult a
          qualified professional before making any investment decisions.
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-500">
        <div>© {year} Axomind Asset Services LLP. All rights reserved.</div>
        <div>Crafted with discipline in Mumbai.</div>
      </div>
    </footer>
  );
}
